import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Box from '@mui/material/Box';

// Custom
import ModelTable from '@/ui/Table/ModelTable';
import TableButtonEdit from '@/ui/Table/ActionButtons/TableButtonEdit';

import dataController from '@/lib/dataController';
import IzletDan from '@/models/izlet_dan';
import DialogContext from '@/ui/DialogContext/DialogContext';
import IzletDanDialog from '../Dialogs/IzletDanDialog';
import LoaderContext from '@/components/LoaderContext/LoaderContext';
import SnackbarContext from '@/ui/SnackbarContext/SnackbarContext';
import TableButtonView from '@/ui/Table/ActionButtons/TableButtonView';
import TableButtonDelete from '@/ui/Table/ActionButtons/TableButtonDelete';
import InfoBox from '@/ui/InfoBox';

// Types
import { DCRecord } from '@/@types/lib/dataController';
import { ConfirmResult } from '@/@types/lib/confirmDialog';

type IIzletDaniPaneProps = {
  variant: 'reg' | 'hps';
  mode: string;
  izletId: number;
  onHasDaniSubmitUvjet?: (value: boolean) => void;
  excursionIsCroatian: boolean;
  showHPSHelpers?: boolean;
};

const IzletDaniPane = (props: IIzletDaniPaneProps) => {
  const { t } = useTranslation();

  const dialogContext = useContext(DialogContext);
  const loaderContext = useContext(LoaderContext);
  const snackbarContext = useContext(SnackbarContext);

  const { variant, mode, izletId, excursionIsCroatian, onHasDaniSubmitUvjet, showHPSHelpers } = props;

  const [records, setRecords] = useState<DCRecord[]>([]);

  const dc = new dataController(
    IzletDan,
    IzletDan.apiPath.replace('{id}', izletId.toString()).replace('hps', variant === 'hps' ? 'hps' : 'reg')
  );

  const title = 'titles.izlet_dani';
  const addLabel = 'buttons.new_dan';

  const confirmDelete = (): Promise<ConfirmResult> => {
    const dialogTitle = t('cdialogs.are_you_sure_delete_dan');
    const text = '';
    const confirmButtonText = t('cdialogs.yes_delete');
    const cancelButtonText = t('cdialogs.no_cancel');

    return dialogContext.showConfirmDialog({ title: dialogTitle, text, confirmButtonText, cancelButtonText });
  };

  useEffect(() => {
    refreshRecords();
  }, []);

  const refreshRecords = () => {
    loaderContext.toggleLoading(true);
    dc.GetData()
      .then((resp) => {
        if (resp.success && Array.isArray(resp.data)) {
          setRecords(resp.data);
          if (resp.data.length === 0) {
            // snackbarContext.showNotification(t('messages.nodata'), 'warning');
          }
        }
      })
      .catch((resp) => {
        console.log(resp);
      })
      .finally(() => {
        loaderContext.toggleLoading(false);
      });
  };

  const handleAdd = () => {
    dialogContext.showDialog(
      IzletDanDialog,
      {
        dc: dc,
        mode: 'insert',
        form: variant,
        onClose: handleDialogClose,
        variant: variant,
        excursionIsCroatian,
      },
      2
    );
  };

  const handleEdit = (evt: React.SyntheticEvent, sel: string[]) => {
    const selId: number = parseInt(sel[0], 10);
    const record: DCRecord = records[selId];
    dialogContext.showDialog(
      IzletDanDialog,
      {
        dc: dc,
        mode: 'update',
        form: variant,
        onClose: handleDialogClose,
        recordId: record.id,
        variant: variant,
        excursionIsCroatian,
      },
      2
    );
  };

  const handleView = (evt: React.SyntheticEvent, sel: string[]) => {
    const selId: number = parseInt(sel[0], 10);
    const record: DCRecord = records[selId];
    dialogContext.showDialog(
      IzletDanDialog,
      {
        dc: dc,
        mode: 'view',
        form: variant,
        onClose: handleDialogClose,
        recordId: record.id,
        variant: variant,
        excursionIsCroatian,
      },
      2
    );
  };

  useEffect(() => {
    if (onHasDaniSubmitUvjet) {
      onHasDaniSubmitUvjet(!!records.filter(dan => dan.has_route).length);
    }
  }, [records]);

  const handleDialogClose = (result: { dataChanged: boolean; action: string; selId?: number }) => {
    if (result.dataChanged) {
      refreshRecords();
    }
  };

  const handleDelete = (evt: React.SyntheticEvent, sel: string[]) => {

    const selIndexes: number[] = sel.map((id) => parseInt(id, 10));

    const selRecords: DCRecord[] =
      selIndexes.map((index) => records[index])

    const recordIds: number[] = selRecords.map((rec) => rec.id).filter((id): id is number => id !== undefined && id !== null);

    confirmDelete().then((result) => {
      if (!result.canceled && result.confirmed) {
        dc.DeleteRecords(recordIds, dc.apiPath).then((resp) => {
          if (resp.success) {
            snackbarContext.showNotification(t('notifications.delete'), 'success');
            refreshRecords();
          } else {
            const msg = t('error.delete') + resp.error;
            snackbarContext.showNotification(msg, 'error');
          }
        });
      }
    });
  };

  const allowCRUD = mode !== 'view';

  return (
    <Box m={0}>
      {showHPSHelpers ?
        <InfoBox labelToken='messages.plan_izleta_helper' sx={{ ml: 3 }} />
        : null}
      <ModelTable
        title={t(title)}
        records={records}
        dc={dc}
        viewName={variant}
        allowSelection="many"
        allowFilter
        allowExport={false}
        allowAdd={allowCRUD}
        handleAdd={handleAdd}
        addLabel={t(addLabel)}
        allowRowAction={() => true}
        onRowAction={() => { }}
      >
        {allowCRUD ? <TableButtonDelete variant="outlined" onClick={handleDelete} /> : null}
        <TableButtonView variant="outlined" onClick={handleView} />
        {allowCRUD ? <TableButtonEdit variant="contained" onClick={handleEdit} /> : null}
      </ModelTable>

    </Box>
  );
};

export default IzletDaniPane;
